import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { tradingApi } from '../services/tradingApi';
import type { ForexNewsEvent } from '../types/trading';
import { BarChart2, Brain, Clock, RefreshCw } from 'lucide-react';
import EventDetailOverlay from '../components/EventDetailOverlay';
import { usePermission } from '../hooks/usePermission';
import { UpgradeNudge } from '../components/UpgradeNudge';

interface PairConfig {
  symbol: string;
  name: string;
  currencies: string[];
}

const FOREX_PAIRS: PairConfig[] = [
  { symbol: 'EURUSD', name: 'Euro / US Dollar',              currencies: ['EUR', 'USD'] },
  { symbol: 'GBPUSD', name: 'British Pound / US Dollar',     currencies: ['GBP', 'USD'] },
  { symbol: 'USDJPY', name: 'US Dollar / Japanese Yen',      currencies: ['USD', 'JPY'] },
  { symbol: 'USDCHF', name: 'US Dollar / Swiss Franc',       currencies: ['USD', 'CHF'] },
  { symbol: 'AUDUSD', name: 'Australian Dollar / US Dollar', currencies: ['AUD', 'USD'] },
  { symbol: 'NZDUSD', name: 'NZ Dollar / US Dollar',         currencies: ['NZD', 'USD'] },
  { symbol: 'USDCAD', name: 'US Dollar / Canadian Dollar',   currencies: ['USD', 'CAD'] },
  { symbol: 'USDZAR', name: 'US Dollar / South African Rand', currencies: ['USD', 'ZAR'] },
];

const COUNTRY_FLAGS: Record<string, string> = {
  USD: '\u{1F1FA}\u{1F1F8}',
  EUR: '\u{1F1EA}\u{1F1FA}',
  GBP: '\u{1F1EC}\u{1F1E7}',
  JPY: '\u{1F1EF}\u{1F1F5}',
  AUD: '\u{1F1E6}\u{1F1FA}',
  NZD: '\u{1F1F3}\u{1F1FF}',
  CAD: '\u{1F1E8}\u{1F1E6}',
  CHF: '\u{1F1E8}\u{1F1ED}',
  ZAR: '\u{1F1FF}\u{1F1E6}',
};

export default function ForexPairs() {
  const { allowed: canViewForexPairs, loading: permLoading } = usePermission('feature:forex-pairs');
  const navigate = useNavigate();
  const [forexEvents, setForexEvents] = useState<ForexNewsEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<ForexNewsEvent | null>(null);

  useEffect(() => {
    if (canViewForexPairs) loadData();
  }, [canViewForexPairs]);

  const loadData = async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      const data = await tradingApi.getForexCalendar();
      setForexEvents(data);
    } catch (err) {
      console.error('Failed to load forex calendar:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const isWithinHours = (dateStr: string, hours: number) => {
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = date.getTime() - now.getTime();
      return diffMs > 0 && diffMs < hours * 3600000;
    } catch {
      return false;
    }
  };

  // Find the latest prediction for a symbol across all events
  const getLatestPrediction = (symbol: string) => {
    let prediction: { bias: string; confidence: number; reasoning?: string | null } | null = null;
    let predictionEventTime: Date | null = null;
    for (const event of forexEvents) {
      const p = (event.predictions ?? []).find((pr) => pr.symbol === symbol);
      if (p) {
        const t = new Date(event.date);
        if (!predictionEventTime || t > predictionEventTime) {
          prediction = p;
          predictionEventTime = t;
        }
      }
    }
    return prediction;
  };

  if (permLoading) return null;

  if (!canViewForexPairs) {
    return (
      <div className="p-6">
        <UpgradeNudge
          tier="pro"
          features={[
            'AI directional bias for 8 major forex pairs',
            'Confidence scores and full AI reasoning',
            'Upcoming high-impact events per pair (next 8h)',
            'Click any event to see detailed AI analysis',
          ]}
        />
      </div>
    );
  }

  return (
    <>
    <div className="p-6">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
            <BarChart2 className="w-8 h-8 text-purple-400" />
            Forex Pairs
          </h1>
          <p className="text-gray-400">
            AI directional bias per major pair, generated from upcoming high-impact economic events.
          </p>
        </div>
        <button
          onClick={() => loadData(true)}
          disabled={refreshing}
          className="flex items-center gap-2 text-sm text-purple-400 hover:text-purple-300 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-gray-800 rounded-lg p-4 space-y-3">
              <div className="h-4 bg-gray-700 rounded animate-pulse w-1/2" />
              <div className="h-8 bg-gray-700 rounded animate-pulse" />
              <div className="h-3 bg-gray-700 rounded animate-pulse w-3/4" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {FOREX_PAIRS.map((pair) => {
            const prediction = getLatestPrediction(pair.symbol);

            // Upcoming high-impact events for the pair's currencies (next 8h)
            const upcomingEvents = forexEvents.filter(
              (e) =>
                pair.currencies.includes(e.country) &&
                e.impact === 'High' &&
                isWithinHours(e.date, 8)
            );

            const biasColor = prediction
              ? prediction.bias === 'BUY'
                ? 'text-green-400'
                : prediction.bias === 'SELL'
                ? 'text-red-400'
                : 'text-gray-400'
              : 'text-gray-500';

            const biasBg = prediction
              ? prediction.bias === 'BUY'
                ? 'bg-green-500/20 border-green-500/30'
                : prediction.bias === 'SELL'
                ? 'bg-red-500/20 border-red-500/30'
                : 'bg-gray-600/20 border-gray-600/30'
              : 'bg-gray-700/30 border-gray-700/30';

            const flags = pair.currencies
              .filter((c) => COUNTRY_FLAGS[c])
              .map((c) => COUNTRY_FLAGS[c])
              .join('');

            return (
              <div key={pair.symbol} className="bg-gray-800 rounded-lg p-4 flex flex-col gap-3">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-base mr-1.5">{flags}</span>
                    <span className="font-bold text-gray-100">{pair.symbol}</span>
                  </div>
                  <span className="text-xs text-gray-500">{pair.name}</span>
                </div>

                {/* Prediction badge */}
                <div className={`rounded-lg border p-3 ${biasBg}`}>
                  {prediction ? (
                    <>
                      <div className="flex items-center gap-2 mb-1">
                        <Brain className="w-4 h-4 text-purple-400" />
                        <span className={`font-bold text-lg ${biasColor}`}>
                          {prediction.bias}
                        </span>
                        <span className="text-gray-400 text-sm">
                          {prediction.confidence}% confidence
                        </span>
                      </div>
                      {prediction.reasoning && (
                        <p className="text-xs text-gray-400 line-clamp-3">
                          {prediction.reasoning}
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center gap-2 text-gray-500 text-sm">
                      <Brain className="w-4 h-4" />
                      <span>No prediction yet — awaiting next cycle</span>
                    </div>
                  )}
                </div>

                {/* Upcoming events */}
                {upcomingEvents.length > 0 ? (
                  <div className="space-y-1.5">
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Upcoming (8h)</p>
                    {upcomingEvents.slice(0, 3).map((e, idx) => (
                      <div
                        key={idx}
                        onClick={() => setSelectedEvent(e)}
                        className="flex items-center gap-2 text-xs cursor-pointer hover:bg-gray-700/40 rounded px-1 py-0.5 transition-colors"
                      >
                        <Clock className="w-3 h-3 text-amber-400 flex-shrink-0" />
                        <span className="text-amber-300 font-mono whitespace-nowrap">
                          {new Date(e.date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        <span className="text-gray-300 truncate">{e.title}</span>
                        <span className="ml-auto flex-shrink-0">
                          {COUNTRY_FLAGS[e.country] || e.country}
                        </span>
                      </div>
                    ))}
                    {upcomingEvents.length > 3 && (
                      <p className="text-xs text-gray-600">+{upcomingEvents.length - 3} more</p>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-gray-600">No high-impact events in next 8h</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>

    {/* Event detail overlay */}
    <EventDetailOverlay event={selectedEvent} onClose={() => setSelectedEvent(null)} />
    </>
  );
}
